import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tab, Tabs, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderList } from '../components';
import { orderService, OrderCountsBySource } from '../services/orderService';

/**
 * Customer Orders page.
 *
 * Two tabs separate orders by source per the stakeholder requirement:
 *   - "Online" — orders the customer placed via this portal (default).
 *   - "Offline" — orders a Mowilex sales rep placed directly in Epicor
 *     on the customer's behalf, imported by the sales-order sync.
 *
 * The badge on each tab shows the current count for that source so
 * customers can see at a glance how many of each they have. Counts
 * come from a single /orders/counts-by-source call rather than two
 * paginated list requests.
 */
const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'portal' | 'epicor'>('portal');
  const [counts, setCounts] = useState<OrderCountsBySource>({
    portal: 0,
    epicor: 0,
    total: 0,
  });

  const refreshCounts = useCallback(async () => {
    try {
      const c = await orderService.getCountsBySource();
      setCounts(c);
    } catch (e) {
      // Counts are advisory; don't block the page if the endpoint hiccups.
      console.error('Failed to load order counts:', e);
    }
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label={t('orders.tabs.ariaLabel', 'Orders source filter')}
        >
          <Tab
            value="portal"
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <span>{t('orders.tabs.online', 'Online')}</span>
                <Chip
                  label={counts.portal}
                  size="small"
                  color={tab === 'portal' ? 'primary' : 'default'}
                  sx={{
                    height: 20,
                    minWidth: 28,
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>
            }
          />
          <Tab
            value="epicor"
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <span>{t('orders.tabs.offline', 'Offline')}</span>
                <Chip
                  label={counts.epicor}
                  size="small"
                  color={tab === 'epicor' ? 'primary' : 'default'}
                  sx={{
                    height: 20,
                    minWidth: 28,
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      <OrderList source={tab} />
    </Box>
  );
};

export default OrdersPage;
